/**
 * Encode/decode city visit bodies (nested in chunks or granular cityVisit-v1).
 */

import type { ItineraryDay, ItineraryItem, Trip, TripCityOption } from '../../types/client';
import { cityAttachmentFileIds } from '../../utils/notesAttachments';
import { parseContentTimestamp } from '../../utils/contentTimestamps';
import { decodeItem, encodeItem, normalizeDeviceSyncItem } from './encodeItem';
import {
  DEVICE_SYNC_EXPORT_SCHEMA_VERSION,
  DEVICE_SYNC_SCHEMA_VERSION,
  type DeviceSyncCityOption,
  type DeviceSyncCityVisit,
  type DeviceSyncDay,
  type DeviceSyncHotelStay,
  type DeviceSyncLargeCityBlobs,
} from './types';

/** Mirror tripsService.normalizeEntityId — kept local to avoid circular imports. */
function normalizeEntityId(id: string | null | undefined): string {
  const raw = String(id ?? '').trim();
  if (!raw) return '';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(raw)) {
    return raw.toLowerCase();
  }
  return raw;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function strOrNull(value: unknown): string | null {
  if (value == null) return null;
  const s = String(value);
  return s.length > 0 ? s : null;
}

function numOrNull(value: unknown): number | null {
  if (value == null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function decodeCityOption(raw: unknown): TripCityOption {
  const o = asRecord(raw);
  return {
    id: String(o.id ?? newId()),
    title: String(o.itemDescription ?? o.title ?? o.name ?? ''),
    description:
      o.optionDescription != null
        ? String(o.optionDescription)
        : o.description != null
          ? String(o.description)
          : undefined,
    url: o.url != null ? String(o.url) : undefined,
    completed: Boolean(o.isDone ?? o.completed ?? o.isCompleted),
    address: o.address != null ? String(o.address) : undefined,
    order: numOrNull(o.order) ?? undefined,
  };
}

export function encodeCityOption(
  option: TripCityOption,
  order = 0
): DeviceSyncCityOption {
  return {
    id: option.id,
    itemDescription: option.title,
    optionDescription: option.description ?? null,
    url: option.url ?? null,
    address: option.address ?? null,
    isDone: Boolean(option.completed),
    order: option.order ?? order,
  };
}

export function hotelStaysToTrip(raw: unknown): import('../../types/client').TripHotelStay[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((h) => {
    const stay = encodeHotelStay(h);
    return {
      id: stay.id,
      hotelName: stay.hotelName,
      checkInDate: stay.checkInDate,
      checkOutDate: stay.checkOutDate,
      address: stay.address,
      phone: stay.phone,
      notes: stay.notes,
      confirmations: stay.confirmations,
    };
  });
}

export function encodeHotelStay(raw: unknown): DeviceSyncHotelStay {
  const o = asRecord(raw);
  const confirmations = Array.isArray(o.confirmations)
    ? o.confirmations.map((c) => String(c))
    : [];
  return {
    id: String(o.id ?? newId()),
    hotelName: String(o.hotelName ?? o.name ?? ''),
    checkInDate: strOrNull(o.checkInDate),
    checkOutDate: strOrNull(o.checkOutDate),
    address: strOrNull(o.address),
    phone: strOrNull(o.phone),
    notes: strOrNull(o.notes),
    confirmations,
  };
}

export function encodeLargeCityBlobs(raw: unknown): DeviceSyncLargeCityBlobs | null {
  if (raw == null) return null;
  const o = asRecord(raw);
  const blobs: DeviceSyncLargeCityBlobs = {};
  if ('toursDataFileId' in o) blobs.toursDataFileId = strOrNull(o.toursDataFileId);
  if ('aiGeneratedPlanDataFileId' in o) {
    blobs.aiGeneratedPlanDataFileId = strOrNull(o.aiGeneratedPlanDataFileId);
  }
  if ('cityPhotoDataFileId' in o) blobs.cityPhotoDataFileId = strOrNull(o.cityPhotoDataFileId);
  if ('toursDataCursor' in o) blobs.toursDataCursor = strOrNull(o.toursDataCursor);
  if ('cityPhotoSource' in o) blobs.cityPhotoSource = strOrNull(o.cityPhotoSource);
  if ('cityPhotoAttribution' in o) {
    blobs.cityPhotoAttribution = strOrNull(o.cityPhotoAttribution);
  }
  return blobs;
}

export function encodeDay(raw: unknown): DeviceSyncDay {
  const o = asRecord(raw);
  const itemsRaw = Array.isArray(o.items) ? o.items : [];
  const updatedAt = parseContentTimestamp(o.updatedAt);
  return {
    id: String(o.id ?? newId()),
    date: String(o.date ?? ''),
    city: strOrNull(o.city),
    timeZone: strOrNull(o.timeZone),
    notes: strOrNull(o.notes),
    updatedAt,
    items: itemsRaw.map((item) => normalizeDeviceSyncItem(item)),
  };
}

export function decodeDay(raw: unknown): {
  day: ItineraryDay;
  cityName: string | null;
  notes: string | null;
  timeZone: string | null;
  dayId: string;
} {
  const wire = encodeDay(raw);
  const date =
    wire.date && /^\d{4}-\d{2}-\d{2}/.test(wire.date)
      ? wire.date.slice(0, 10)
      : wire.date;
  const items: ItineraryItem[] = wire.items
    .filter((i) => !i.deletedAt)
    .map((i) => decodeItem(i).item);
  return {
    day: {
      date,
      items,
      id: wire.id,
      notes: wire.notes,
      timeZone: wire.timeZone,
      updatedAt: wire.updatedAt ?? null,
    },
    cityName: wire.city,
    notes: wire.notes,
    timeZone: wire.timeZone,
    dayId: wire.id,
  };
}

/**
 * Decode a Device Sync city visit dict (chunk nested or granular body).
 */
export function decodeCityVisit(raw: unknown): DeviceSyncCityVisit {
  const o = asRecord(raw);
  const hotelStays = Array.isArray(o.hotelStays)
    ? o.hotelStays.map((h) => encodeHotelStay(h))
    : [];
  const options = Array.isArray(o.options)
    ? o.options.map((opt, index) => {
        const optRec = asRecord(opt);
        return {
          id: String(optRec.id ?? newId()),
          itemDescription: String(optRec.itemDescription ?? optRec.title ?? ''),
          optionDescription: strOrNull(optRec.optionDescription ?? optRec.description),
          url: strOrNull(optRec.url),
          address: strOrNull(optRec.address),
          isDone: Boolean(optRec.isDone ?? optRec.completed),
          order: numOrNull(optRec.order) ?? index,
        };
      })
    : [];
  const days = Array.isArray(o.days) ? o.days.map((d) => encodeDay(d)) : [];
  const attachmentFileIds = Array.isArray(o.attachmentFileIds)
    ? o.attachmentFileIds.map((id) => String(id))
    : [];

  const cityVisitId = normalizeEntityId(
    String(o.cityVisitId ?? o.id ?? '')
  );

  return {
    cityVisitId,
    cityName: String(o.cityName ?? o.city ?? o.name ?? ''),
    latitude: numOrNull(o.latitude),
    longitude: numOrNull(o.longitude),
    timeZoneIdentifier: strOrNull(o.timeZoneIdentifier),
    arrivalDate: strOrNull(o.arrivalDate),
    departureDate: strOrNull(o.departureDate),
    countryName: strOrNull(o.countryName),
    countryISO2: strOrNull(o.countryISO2),
    currencyCode: strOrNull(o.currencyCode),
    isCruisePort: o.isCruisePort == null ? null : Boolean(o.isCruisePort),
    cruisePortStatus: strOrNull(o.cruisePortStatus),
    dockNumber: strOrNull(o.dockNumber),
    portArrivalTime: strOrNull(o.portArrivalTime),
    portDepartureTime: strOrNull(o.portDepartureTime),
    hotelStays,
    options,
    largeCityBlobs: encodeLargeCityBlobs(o.largeCityBlobs),
    attachmentFileIds,
    days,
    tripId: o.tripId != null ? normalizeEntityId(String(o.tripId)) : undefined,
    deleted: o.deleted === true,
    schemaVersion:
      typeof o.schemaVersion === 'number' ? o.schemaVersion : undefined,
    exportSchemaVersion:
      o.exportSchemaVersion != null ? String(o.exportSchemaVersion) : undefined,
  };
}

/**
 * Encode a decoded city visit back to a plain JSON-compatible body
 * (stable key set for round-trip / coverage).
 */
export function encodeCityVisit(visit: DeviceSyncCityVisit): Record<string, unknown> {
  const body: Record<string, unknown> = {
    cityVisitId: visit.cityVisitId,
    cityName: visit.cityName,
    latitude: visit.latitude ?? null,
    longitude: visit.longitude ?? null,
    timeZoneIdentifier: visit.timeZoneIdentifier ?? null,
    arrivalDate: visit.arrivalDate ?? null,
    departureDate: visit.departureDate ?? null,
    countryName: visit.countryName ?? null,
    countryISO2: visit.countryISO2 ?? null,
    currencyCode: visit.currencyCode ?? null,
    isCruisePort: visit.isCruisePort ?? null,
    cruisePortStatus: visit.cruisePortStatus ?? null,
    dockNumber: visit.dockNumber ?? null,
    portArrivalTime: visit.portArrivalTime ?? null,
    portDepartureTime: visit.portDepartureTime ?? null,
    hotelStays: visit.hotelStays,
    options: visit.options,
    attachmentFileIds: visit.attachmentFileIds,
    days: visit.days,
  };
  if (visit.largeCityBlobs) {
    body.largeCityBlobs = visit.largeCityBlobs;
  }
  if (visit.tripId) body.tripId = visit.tripId;
  if (visit.deleted != null) body.deleted = visit.deleted;
  if (visit.schemaVersion != null) body.schemaVersion = visit.schemaVersion;
  if (visit.exportSchemaVersion != null) {
    body.exportSchemaVersion = visit.exportSchemaVersion;
  }
  return body;
}

/**
 * Build a granular cityVisit-v1 body from a web Trip + cityVisitId,
 * including itinerary days/items (stops empty `days: []`).
 */
export function buildGranularCityVisitBody(
  trip: Trip,
  cityVisitId: string
): Record<string, unknown> {
  const id = normalizeEntityId(cityVisitId);
  const meta = trip.cityVisitMeta?.[id] ?? trip.cityVisitMeta?.[cityVisitId];
  const coords = trip.cityCoords?.[id] ?? trip.cityCoords?.[cityVisitId];
  const cityName =
    trip.cityNames?.[id] || trip.cityNames?.[cityVisitId] || 'City';
  const options = (trip.cityOptions?.[id] ?? trip.cityOptions?.[cityVisitId] ?? []).map(
    (o, index) => encodeCityOption(o, index)
  );

  const daysForCity = (trip.itineraryDays ?? []).filter((d) => {
    const dayCityId = normalizeEntityId(d.cityId);
    return dayCityId === id || (!dayCityId && (trip.cityIds?.length ?? 0) === 1);
  });

  const days: DeviceSyncDay[] = daysForCity.map((d) => {
    const dayId = d.id || `${trip.id}_${d.date}`;
    const day: DeviceSyncDay = {
      id: dayId,
      date: `${d.date}T00:00:00Z`,
      city: cityName,
      timeZone: d.timeZone ?? null,
      notes: d.notes ?? null,
      items: d.items.map((item) =>
        encodeItem(item, { dayDate: d.date, timeZone: d.timeZone })
      ),
    };
    if (d.updatedAt) day.updatedAt = d.updatedAt;
    return day;
  });

  const hotelStays = (
    trip.hotelStaysByCity?.[id] ?? trip.hotelStaysByCity?.[cityVisitId] ?? []
  ).map((h) =>
    encodeHotelStay({
      id: h.id,
      hotelName: h.hotelName,
      checkInDate: h.checkInDate,
      checkOutDate: h.checkOutDate,
      address: h.address,
      phone: h.phone,
      notes: h.notes,
      confirmations: h.confirmations ?? [],
    })
  );

  const body: Record<string, unknown> = {
    schemaVersion: DEVICE_SYNC_SCHEMA_VERSION,
    exportSchemaVersion: DEVICE_SYNC_EXPORT_SCHEMA_VERSION,
    tripId: trip.id,
    cityVisitId: id,
    deleted: false,
    cityName,
    options,
    hotelStays,
    days,
    attachmentFileIds: cityAttachmentFileIds(trip, id),
  };

  if (meta?.arrivalDate) body.arrivalDate = `${meta.arrivalDate}T00:00:00Z`;
  if (meta?.departureDate) body.departureDate = `${meta.departureDate}T00:00:00Z`;
  if (meta?.portArrivalHm && meta.arrivalDate) {
    body.portArrivalTime = `${meta.arrivalDate}T${meta.portArrivalHm}:00Z`;
  } else if (meta?.portArrivalHm) {
    body.portArrivalTime = meta.portArrivalHm;
  }
  if (meta?.portDepartureHm && meta.departureDate) {
    body.portDepartureTime = `${meta.departureDate}T${meta.portDepartureHm}:00Z`;
  } else if (meta?.portDepartureHm) {
    body.portDepartureTime = meta.portDepartureHm;
  }
  if (meta?.cruisePortStatus) {
    const status = meta.cruisePortStatus;
    body.cruisePortStatus =
      status.charAt(0).toUpperCase() + status.slice(1);
    body.isCruisePort = status === 'docked' || status === 'tendered';
  }
  if (meta?.dockNumber) body.dockNumber = meta.dockNumber;
  if (meta?.countryName) body.countryName = meta.countryName;
  if (meta?.countryISO2) body.countryISO2 = meta.countryISO2;
  if (meta?.currencyCode) body.currencyCode = meta.currencyCode;
  if (meta?.timeZoneIdentifier) body.timeZoneIdentifier = meta.timeZoneIdentifier;
  if (meta?.aiJobId) body.aiJobId = meta.aiJobId;
  if (meta?.aiJobStartedAt) body.aiJobStartedAt = meta.aiJobStartedAt;
  if (meta?.aiStatus) body.aiStatus = meta.aiStatus;
  if (meta?.aiUpdatedAt) body.aiUpdatedAt = meta.aiUpdatedAt;
  if (coords) {
    body.latitude = coords.latitude;
    body.longitude = coords.longitude;
  }

  return body;
}

/**
 * Convert decoded city visit days into web ItineraryDay[] with cityId set.
 */
export function itineraryDaysFromCityVisit(
  visit: DeviceSyncCityVisit
): ItineraryDay[] {
  return visit.days.map((d) => {
    const decoded = decodeDay(d);
    return {
      ...decoded.day,
      cityId: visit.cityVisitId,
    };
  });
}

export function mealTitlesFromCityVisit(visit: DeviceSyncCityVisit): string[] {
  return visit.days
    .flatMap((d) => d.items)
    .filter((i) => i.type === 'meal' && !i.deletedAt)
    .map((i) => i.title);
}
